/**
 * The Archer (PRD #800): the class that proves range.
 *
 * Everything here is declaration. The kit is five catalog ability ids with the
 * levels that unlock them, the weapon family is a bow, and the equipment is
 * cloth the bake already carries. There is no Archer code anywhere: the bow
 * shot is the kernel's `projectile` shape, the trap is its `tile` shape, and the
 * roll away is the same `dash` the Warrior charges with.
 *
 * The kit staggers across the campaign rather than arriving whole, which is the
 * first use of the unlock machinery PRD #792 built and left at level one for
 * every Warrior ability. Quick Shot is the level-one bow; Volley, Snipe,
 * Disengage, and Snare Trap arrive at 3, 5, 7, and 9.
 *
 * Every number is first-pass, in the posture of #792 through #795, and none of
 * it is defended until the tuning PRD (#799).
 */

import type { ArpgClassDef } from './types';

/** The mana cadence the dungeon runs: one point every five simulation ticks. */
const SHIPPED_MANA_REGEN_PER_TICK = 1 / 5;

export const ARCHER_CLASS: ArpgClassDef = {
  id: 'archer',
  name: 'Archer',
  blurb: 'A ranged Hero who answers pressure with distance, a bow, and a trap.',
  playable: true,
  weapons: {
    // A bow deals less per shot than a halberd and lands it from across the
    // room, which is the whole trade the class exists to make.
    bow: { damage: 14, grip: 'two-handed' },
    crossbow: { damage: 17, grip: 'two-handed' },
  },
  allowedEquipmentIds: new Set([
    'tunic',
    'pants.long',
    'cloak.plain',
    'boots.thigh',
    'boots.plain',
    'shoes.plain',
    'hat.feathered',
    'hat.cap',
    'bow',
    'crossbow',
  ]),
  // The cloak is allowed but not worn: it draws over the drawn bow, and a class
  // whose whole identity is the weapon in its hands has to be readable holding
  // it. Checked in the client, not assumed.
  curves: {
    // Lighter than the Warrior at every level: 95 to 190 against 120 to 310.
    // Range is the defense the class is paying for.
    maxHealth: { base: 95, perLevel: 5 },
    maxMana: { base: 50, perLevel: 2 },
    healthRegenPerTick: 0,
    manaRegenPerTick: SHIPPED_MANA_REGEN_PER_TICK,
    critChance: 0,
    critMultiplier: 1.5,
  },
  abilities: [
    { abilityId: 'archer-quick-shot', unlockLevel: 1, slot: 'right-mouse' },
    { abilityId: 'archer-volley', unlockLevel: 3, slot: 'key-3' },
    // The aimed shot goes under the same finger the Warrior thrusts with: hold
    // to commit, and the wind-up is the cost of holding still.
    { abilityId: 'archer-snipe', unlockLevel: 5, slot: 'left-mouse' },
    { abilityId: 'archer-disengage', unlockLevel: 7, slot: 'key-4' },
    { abilityId: 'archer-snare-trap', unlockLevel: 9, slot: 'key-5' },
  ],
  startingEquipment: {
    mainHandId: 'bow',
    equipped: [
      { id: 'tunic', layer: 'shirt', hue: 1836 },
      { id: 'pants.long', layer: 'pants', hue: 1815 },
      { id: 'hat.feathered', layer: 'headgear', hue: 1836 },
      { id: 'boots.thigh', layer: 'feet', hue: 1109 },
      { id: 'bow', layer: 'weapon' },
    ],
  },
  balanceFloor: {
    // Quick Shot deals 19 with the starting bow and recovers in eight ticks, so
    // the 70-health placeholder dies on the fourth shot, twenty-four ticks in,
    // plus the flight time the arrows spend crossing the gap.
    maxTicksToKillPlaceholder: 45,
    // 95 health against a twelve-damage swing is eight hits, which is the
    // margin a class that is not supposed to be standing there gets.
    minPlaceholderSwingsSurvived: 7,
    // Snipe is the costliest thing the kit casts, at twelve of a fifty pool.
    minCostliestCastsFromFullMana: 4,
  },
};
